import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { URL, getToken } from "../../utils";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useCountries } from "../../hooks/useCountries";
import Select from "react-select";
import CountryField from "../../components/CountryField";
import { getCountryCode } from "../../utils/countryUtils";

export default function UserEditForm() {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    role: "",
    which_country: "",
    magasin: "",
    is_caissier: false,
    poste: "",
    is_staff: false,
    is_active: true,
    password: "",
    confirm_password: "",
  });

  const [magasins, setMagasins] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isLoadingMagasins, setIsLoadingMagasins] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    text: "",
    color: "",
    checks: {},
  });
  const { countries } = useCountries();
  const [originalCountry, setOriginalCountry] = useState("");
  const navigate = useNavigate();
  const token = getToken();

  const fetchUser = useCallback(async () => {
    if (!id) return;

    setIsLoadingUser(true);
    try {
      const response = await axios.get(
        `${URL}authentification/user-detail/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const userData = response.data;
      setFormData({
        username: userData.username || "",
        email: userData.email || "",
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        phone_number: userData.phone_number || "",
        role: userData.role || "",
        which_country: userData.which_country || "",
        magasin: userData.magasin || "",
        is_caissier: userData.is_caissier || false,
        poste: userData.poste || "",
        is_staff: userData.is_staff || false,
        is_active: userData.is_active !== undefined ? userData.is_active : true,
        password: "",
      });
      // Conserver le nom du pays renvoyé par l'API pour le convertir en code.
      setOriginalCountry(userData.which_country || "");
    } catch (error) {
      toast.error("Erreur lors du chargement de l'utilisateur");
      console.error("Erreur:", error);
      navigate("/users");
    } finally {
      setIsLoadingUser(false);
    }
  }, [id, token, navigate]);

  const fetchMagasins = useCallback(async () => {
    setIsLoadingMagasins(true);
    try {
      const response = await axios.get(`${URL}Magasins/magasins-list`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setMagasins(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des magasins");
    } finally {
      setIsLoadingMagasins(false);
    }
  }, [token]);

  // Évaluer la force du mot de passe
  const getPasswordStrength = useCallback((password) => {
    if (!password) return { score: 0, text: "", color: "", checks: {} };

    const checks = {
      length: password.length >= 6,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      special: /[@$!%*?&]/.test(password),
    };

    const score = Object.values(checks).filter(Boolean).length;

    const strengthLevels = {
      0: { text: "", color: "" },
      1: { text: "Très faible", color: "bg-red-500" },
      2: { text: "Faible", color: "bg-orange-500" },
      3: { text: "Moyen", color: "bg-yellow-500" },
      4: { text: "Fort", color: "bg-blue-500" },
      5: { text: "Très fort", color: "bg-green-500" },
    };

    return { score, ...strengthLevels[score], checks };
  }, []);

  // Validation en temps réel du mot de passe
  const validatePasswordLive = useCallback(
    (password) => {
      const strength = getPasswordStrength(password);
      setPasswordStrength(strength);

      if (!password) {
        setValidationErrors((prev) => ({
          ...prev,
          password: "",
        }));
        return;
      }

      const errors = [];

      if (!strength.checks.length) {
        errors.push("au moins 6 caractères");
      }
      if (!strength.checks.lowercase) errors.push("une lettre minuscule");
      if (!strength.checks.uppercase) errors.push("une lettre majuscule");
      if (!strength.checks.numbers) errors.push("un chiffre");
      if (!strength.checks.special)
        errors.push("un caractère spécial (@$!%*?&)");

      const errorMessage =
        errors.length > 0
          ? `Le mot de passe doit contenir ${errors.join(", ")}`
          : "";

      setValidationErrors((prev) => ({
        ...prev,
        password: errorMessage,
      }));
    },
    [getPasswordStrength, setPasswordStrength],
  );

  // Validation en temps réel de la confirmation du mot de passe
  const validateConfirmPasswordLive = useCallback(
    (confirmPassword) => {
      if (!confirmPassword) {
        setValidationErrors((prev) => ({
          ...prev,
          confirm_password: "",
        }));
        return;
      }

      if (formData.password && formData.password !== confirmPassword) {
        setValidationErrors((prev) => ({
          ...prev,
          confirm_password: "Les mots de passe ne correspondent pas",
        }));
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          confirm_password: "",
        }));
      }
    },
    [formData.password],
  );

  useEffect(() => {
    fetchUser();
    fetchMagasins();
  }, [fetchUser, fetchMagasins]);

  // Convertit le nom du pays renvoyé par l'API en code ISO une fois la liste
  // des pays chargée, afin que l'option par défaut soit bien sélectionnée.
  useEffect(() => {
    if (originalCountry && countries.length > 1) {
      setFormData((prev) => ({
        ...prev,
        which_country: getCountryCode(originalCountry, countries),
      }));
    }
  }, [originalCountry, countries]);

  // Re-valider la confirmation du mot de passe quand le mot de passe principal change
  useEffect(() => {
    if (formData.confirm_password) {
      validateConfirmPasswordLive(formData.confirm_password);
    }
  }, [
    formData.password,
    formData.confirm_password,
    validateConfirmPasswordLive,
  ]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Live validation pour le mot de passe
    if (name === "password" && value) {
      validatePasswordLive(value);
    }

    // Live validation pour la confirmation du mot de passe
    if (name === "confirm_password" && value) {
      validateConfirmPasswordLive(value);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = "Le nom d'utilisateur est requis";
    }

    if (!formData.email.trim()) {
      errors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Format d'email invalide";
    }

    if (!formData.role) {
      errors.role = "Le rôle est requis";
    }

    if (!formData.which_country) {
      errors.which_country = "Le pays est requis";
    }

    // Validation du mot de passe uniquement si défini
    if (formData.password) {
      // Re-valider le mot de passe lors de la soumission
      validatePasswordLive(formData.password);
      if (validationErrors.password) {
        errors.password = validationErrors.password;
      }

      // Valider la confirmation du mot de passe
      if (
        formData.confirm_password &&
        formData.password !== formData.confirm_password
      ) {
        errors.confirm_password = "Les mots de passe ne correspondent pas";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Préparer les données à envoyer
      const submitData = {
        ...formData,
        which_country: getCountryCode(formData.which_country, countries),
        magasin: formData.magasin || null,
        password: formData.password || null,
      };

      await axios.put(`${URL}authentification/user-update/${id}`, submitData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Utilisateur modifié avec succès");
      navigate("/users");
    } catch (error) {
      console.error("Erreur:", error);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "object") {
          setValidationErrors(errorData);
          toast.error("Veuillez corriger les erreurs dans le formulaire");
        } else {
          toast.error(
            errorData.message ||
              "Erreur lors de la modification de l'utilisateur",
          );
        }
      } else {
        toast.error("Erreur lors de la modification de l'utilisateur");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const roles = [
    { value: "direction", label: "Direction" },
    { value: "country_manager", label: "Country Manager" },
    { value: "resp_commercial", label: "Responsable Commercial" },
    { value: "resp_boutique", label: "Responsable Boutique" },
    { value: "vendeur", label: "Vendeur" },
    { value: "ges_stock", label: "Gestionnaire de Stock" },
    { value: "boutiquier", label: "Boutiquier" },
    { value: "administrateur", label: "Administrateur" },
  ];

  if (isLoadingUser) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <ToastContainer />

      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Modifier l'utilisateur
          </h1>
          <button
            onClick={() => navigate("/users")}
            className="text-gray-600 hover:text-gray-800"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations de connexion */}
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Informations de connexion
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-700"
                >
                  Nom d'utilisateur *
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500 ${
                    validationErrors.username ? "border-red-500" : ""
                  }`}
                  placeholder="Nom d'utilisateur unique"
                />
                {validationErrors.username && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.username}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500 ${
                    validationErrors.email ? "border-red-500" : ""
                  }`}
                  placeholder="email@exemple.com"
                />
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 flex max-md:flex-col gap-4">
              <div className="flex-1">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Nouveau mot de passe
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500 ${
                    validationErrors.password ? "border-red-500" : ""
                  }`}
                  placeholder="Laisser vide pour ne pas changer"
                />

                {/* Indicateur de force du mot de passe */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                          style={{
                            width: `${(passwordStrength.score / 5) * 100}%`,
                          }}
                        ></div>
                      </div>
                      {passwordStrength.text && (
                        <span className="text-xs font-medium text-gray-600">
                          {passwordStrength.text}
                        </span>
                      )}
                    </div>

                    {/* Checklist des critères */}
                    <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                      <span
                        className={`flex items-center ${
                          passwordStrength.checks.length
                            ? "text-green-600"
                            : "text-gray-400"
                        }`}
                      >
                        {passwordStrength.checks.length ? "✓" : "○"} 6+
                        caractères
                      </span>
                      <span
                        className={`flex items-center ${
                          passwordStrength.checks.lowercase
                            ? "text-green-600"
                            : "text-gray-400"
                        }`}
                      >
                        {passwordStrength.checks.lowercase ? "✓" : "○"}{" "}
                        Minuscule
                      </span>
                      <span
                        className={`flex items-center ${
                          passwordStrength.checks.uppercase
                            ? "text-green-600"
                            : "text-gray-400"
                        }`}
                      >
                        {passwordStrength.checks.uppercase ? "✓" : "○"}{" "}
                        Majuscule
                      </span>
                      <span
                        className={`flex items-center ${
                          passwordStrength.checks.numbers
                            ? "text-green-600"
                            : "text-gray-400"
                        }`}
                      >
                        {passwordStrength.checks.numbers ? "✓" : "○"} Chiffre
                      </span>
                      <span
                        className={`flex items-center ${
                          passwordStrength.checks.special
                            ? "text-green-600"
                            : "text-gray-400"
                        }`}
                      >
                        {passwordStrength.checks.special ? "✓" : "○"} Spécial
                        (@$!%*?&)
                      </span>
                    </div>
                  </div>
                )}

                {validationErrors.password && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.password}
                  </p>
                )}
              </div>
              {/* Live Confirm Password */}
              <div className="flex-1">
                <label
                  htmlFor="confirm_password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirmer le mot de passe
                </label>
                <input
                  type="password"
                  id="confirm_password"
                  name="confirm_password"
                  value={formData.confirm_password || ""}
                  onChange={handleChange}
                  className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500 ${
                    validationErrors.confirm_password ? "border-red-500" : ""
                  }`}
                  placeholder="Confirmer le mot de passe"
                />
                {validationErrors.confirm_password && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.confirm_password}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Informations personnelles */}
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Informations personnelles
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="first_name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Prénom
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Prénom"
                />
              </div>

              <div>
                <label
                  htmlFor="last_name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Nom
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Nom de famille"
                />
              </div>

              <div>
                <label
                  htmlFor="phone_number"
                  className="block text-sm font-medium text-gray-700"
                >
                  Téléphone
                </label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                  placeholder="+228 XX XX XX XX"
                />
              </div>

              <div>
                <label
                  htmlFor="poste"
                  className="block text-sm font-medium text-gray-700"
                >
                  Poste
                </label>
                <input
                  type="text"
                  id="poste"
                  name="poste"
                  value={formData.poste}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Fonction dans l'entreprise"
                />
              </div>
            </div>
          </div>

          {/* Rôle et permissions */}
          <div className="border-b border-gray-200 pb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Rôle et permissions
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="role"
                  className="block text-sm font-medium text-gray-700"
                >
                  Rôle *
                </label>
                <Select
                  id="role"
                  name="role"
                  value={
                    roles.find((role) => role.value === formData.role) || null
                  }
                  onChange={(selectedOption) => {
                    setFormData((prev) => ({
                      ...prev,
                      role: selectedOption ? selectedOption.value : "",
                    }));
                    if (validationErrors.role) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        role: "",
                      }));
                    }
                  }}
                  options={roles}
                  placeholder="Sélectionner un rôle..."
                  noOptionsMessage={() => "Aucun résultat"}
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: validationErrors.role
                        ? "#ef4444"
                        : "#d1d5db",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#f59e0b",
                      },
                    }),
                  }}
                />
                {validationErrors.role && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.role}
                  </p>
                )}
              </div>

              <div>
                <CountryField
                  countries={countries}
                  value={formData.which_country}
                  onChange={(selectedOption) => {
                    setFormData((prev) => ({
                      ...prev,
                      which_country: selectedOption ? selectedOption.value : "",
                    }));
                    if (validationErrors.which_country) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        which_country: "",
                      }));
                    }
                  }}
                  label="Pays"
                  validationErrors={validationErrors}
                  fieldName="which_country"
                  required={true}
                />
              </div>

              <div>
                <label
                  htmlFor="magasin"
                  className="block text-sm font-medium text-gray-700"
                >
                  Magasin
                </label>
                <Select
                  id="magasin"
                  name="magasin"
                  value={
                    magasins.find((magasin) => magasin.id === formData.magasin)
                      ? {
                          value: formData.magasin,
                          label: magasins.find(
                            (magasin) => magasin.id === formData.magasin,
                          ).nom,
                        }
                      : null
                  }
                  onChange={(selectedOption) => {
                    setFormData((prev) => ({
                      ...prev,
                      magasin: selectedOption ? selectedOption.value : "",
                    }));
                  }}
                  options={magasins.map((magasin) => ({
                    value: magasin.id,
                    label: magasin.nom,
                  }))}
                  placeholder="Aucun magasin"
                  noOptionsMessage={() => "Aucun résultat"}
                  isLoading={isLoadingMagasins}
                  isDisabled={isLoadingMagasins}
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#d1d5db",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#f59e0b",
                      },
                    }),
                  }}
                />
                {isLoadingMagasins && (
                  <p className="mt-1 text-sm text-gray-500">
                    Chargement des magasins...
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-col space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_caissier"
                  name="is_caissier"
                  checked={formData.is_caissier}
                  onChange={handleChange}
                  className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is_caissier"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Est caissier
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_staff"
                  name="is_staff"
                  checked={formData.is_staff}
                  onChange={handleChange}
                  className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is_staff"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Accès administration
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is_active"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Compte actif
                </label>
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/users")}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50"
            >
              {isLoading ? "Modification..." : "Modifier l'utilisateur"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
